import { msg } from "@lingui/core/macro";
import { createRoute, Link, useNavigate, useSearch } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, onMount, Show } from "solid-js";
import { Boxes, Download, Tags, X } from "@fleet/icons";
import {
  ADJUSTMENT_REASONS,
  adjustStockMutation,
  buildStockDataset,
  exportDataset,
  type ExportFormat,
  fromMinorQty,
  getLocationsQuery,
  getProductsQuery,
  getStockQuantsQuery,
  parseQtyInput,
  queryKeys,
} from "@stockpilot/shared";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { useResetOnOrgSwitch } from "@/features/inventory/use-reset-on-org-switch";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { EmptyShelvesIllustration } from "@/components/ui/illustrations";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { deriveStockStatus, StockBadge } from "@/components/ui/stock-badge";
import type { LocationRecord, ProductRecord, StockQuantRecord } from "@stockpilot/powersync";

function StockPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();
  const search = useSearch({ from: "/_app/stock" });
  const navigate = useNavigate();

  const [productId, setProductId] = createSignal("");
  const [locationId, setLocationId] = createSignal("");
  const [qtyInput, setQtyInput] = createSignal("");
  // REQUIRED reason for the adjustment (ERP requirement §1b: server rejects moves
  // without reason) + optional free-form note.
  const [reason, setReason] = createSignal("");
  const [note, setNote] = createSignal("");

  // Router deferred flush guard: if boot lost the `?product=` of the
  // initial deep link, reapply it synchronously from the snapshot.
  onMount(() => {
    const initialProduct = takeInitialSearchParam("product", "/stock");
    if (initialProduct && !(search().product ?? "")) {
      void navigate({ to: "/stock", search: { product: initialProduct }, replace: true });
    }
  });

  // Deep-link filter: /stock?product=<id> focuses the list on one product and
  // pre-selects it in the adjustment form. Cleared via the filter chip.
  const productFilter = (): string => search().product ?? "";
  createEffect(() => {
    const pid = productFilter();
    if (pid) {
      setProductId(pid);
    }
  });
  const clearProductFilter = () => {
    void navigate({ to: "/stock", search: {} });
  };

  // Multi-organization isolation: when switching companies, the `?product=` filter
  // and adjustment form selections (product/location) would point to
  // entities from the PREVIOUS org (local cache holds all my orgs at once).
  // An adjustment with a foreign product_id/location_id would be written to the new org
  // → integrity leak. We discard filter and selections when switching orgs.
  useResetOnOrgSwitch(() => {
    void navigate({ to: "/stock", search: {} });
    setProductId("");
    setLocationId("");
  });

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const locations = createPowerSyncQuery<LocationRecord>({
    queryKey: () => queryKeys.inventory.locations(orgId()),
    queryFn: () => getLocationsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const quants = createPowerSyncQuery<StockQuantRecord>({
    queryKey: () => queryKeys.inventory.quants(orgId()),
    queryFn: () => getStockQuantsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const productName = createMemo(() => {
    const map = new Map((products.data ?? []).map((p) => [p.id, p.name]));
    return (id: string) => map.get(id) ?? id;
  });

  // Visible rows honor the deep-link product filter.
  const visibleQuants = createMemo(() => {
    const pid = productFilter();
    const all = quants.data ?? [];
    return pid ? all.filter((q) => q.product_id === pid) : all;
  });
  const locationName = createMemo(() => {
    const map = new Map((locations.data ?? []).map((l) => [l.id, l.name]));
    return (id: string) => map.get(id) ?? id;
  });

  async function handleAdjust(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const pid = productId();
    const lid = locationId();
    const target = parseQtyInput(qtyInput());
    if (!d || !pid || !lid || target === null || !reason()) {
      return;
    }
    // CC-7: blocking save (await), not fire-and-forget. Adjustments do NOT
    // register L1 undo: a confirmed move is only corrected with an
    // explicit reversing move (activity feed).
    //
    // `adjustStockMutation` may THROW (verified phantom UPDATE, see
    // `@stockpilot/shared/inventory/mutations` + `powersync/verified-write`):
    // without this catch, a rejection here would surface as unhandled rejection
    // instead of notifying the user that the adjustment was not saved.
    try {
      await adjustStockMutation(d, orgId(), pid, lid, target, reason(), null, note());
      setQtyInput("");
      setNote("");
      showToast(t(msg`Stock adjusted`), "success");
    } catch {
      showToast(t(msg`Could not save the stock adjustment`), "error");
    }
  }

  const reasonOptions = createMemo(() =>
    ADJUSTMENT_REASONS.map((value) => ({ value, label: reasonLabel(value) })),
  );

  const delivery = useKernelService(EXPORT_DELIVERY);

  // K1 — Export of VISIBLE on-hand (honors product filter) to CSV/PDF.
  // Reuses the id→name resolvers already memoized for the table.
  const handleExport = (format: ExportFormat) => {
    const target = delivery();
    if (!target) {
      return;
    }
    const dataset = buildStockDataset(
      visibleQuants(),
      { productName: productName(), locationName: locationName() },
      { title: t(msg`Stock`), subtitle: t(msg`Exported on-hand quantities`) },
    );
    void exportDataset(dataset, format, target);
  };

  function reasonLabel(value: string): string {
    switch (value) {
      case "merma": {
        return t(msg`Shrinkage`);
      }
      case "rotura": {
        return t(msg`Breakage`);
      }
      case "robo": {
        return t(msg`Theft`);
      }
      case "recuento": {
        return t(msg`Recount`);
      }
      default: {
        return t(msg`Other`);
      }
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Stock`)}
        description={t(msg`On-hand quantities and inventory adjustments.`)}
        icon={Boxes}
      />

      <form
        class="mt-6 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleAdjust}
        data-testid="adjust-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Product`)}</label>
          <Select
            class="w-44"
            value={productId()}
            onChange={(v) => setProductId(v)}
            data-testid="adjust-product-select"
            aria-label={t(msg`Product`)}
            placeholder={t(msg`Select product`)}
            options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            emptyAction={{
              label: t(msg`Add a product`),
              description: t(msg`No products yet`),
              onSelect: () => void navigate({ to: "/products" }),
            }}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Location`)}</label>
          <Select
            class="w-44"
            value={locationId()}
            onChange={(v) => setLocationId(v)}
            data-testid="adjust-location-select"
            aria-label={t(msg`Location`)}
            placeholder={t(msg`Select location`)}
            options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
            emptyAction={{
              label: t(msg`Add a location`),
              description: t(msg`No locations yet`),
              onSelect: () => void navigate({ to: "/warehouses" }),
            }}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Counted quantity`)}</label>
          <input
            class="w-32 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder="0"
            inputmode="decimal"
            value={qtyInput()}
            onInput={(e) => setQtyInput(e.currentTarget.value)}
            data-testid="adjust-qty-input"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Reason (required)`)}</label>
          <Select
            class="w-40"
            value={reason()}
            onChange={(v) => setReason(v)}
            data-testid="adjust-reason-select"
            aria-label={t(msg`Reason (required)`)}
            placeholder={t(msg`Select a reason`)}
            options={reasonOptions()}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Note (optional)`)}</label>
          <input
            class="w-44 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            value={note()}
            onInput={(e) => setNote(e.currentTarget.value)}
            data-testid="adjust-note-input"
            placeholder={t(msg`What happened?`)}
          />
        </div>
        <Button type="submit" disabled={!drizzle() || !reason()} data-testid="adjust-submit-button">
          {t(msg`Apply adjustment`)}
        </Button>
      </form>

      <div class="mt-8 flex flex-wrap items-center gap-3">
        <h2 class="text-lg font-semibold tracking-tight">{t(msg`On-hand`)}</h2>
        <Show when={productFilter()}>
          <button
            type="button"
            onClick={clearProductFilter}
            class="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
            data-testid="stock-filter-chip"
          >
            {(() => {
              const filteredName = productName()(productFilter());
              return t(msg`Filtered: ${filteredName}`);
            })()}
            <X class="h-3.5 w-3.5" />
          </button>
        </Show>
        {/* K1: export of visible on-hand (hidden without data). */}
        <Show when={visibleQuants().length > 0}>
          <div class="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleExport("csv")}
              data-testid="export-csv-button"
            >
              <Download class="h-4 w-4" />
              CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleExport("pdf")}
              data-testid="export-pdf-button"
            >
              <Download class="h-4 w-4" />
              PDF
            </Button>
          </div>
        </Show>
      </div>
      <div class="mt-3" data-testid="onhand-list">
        <Show
          when={visibleQuants().length > 0}
          fallback={
            <EmptyState
              icon={Boxes}
              illustration={EmptyShelvesIllustration}
              title={productFilter() ? t(msg`No stock for this product`) : t(msg`No stock yet`)}
              description={
                productFilter()
                  ? t(msg`This product has no on-hand quantity yet.`)
                  : t(msg`Apply an inventory adjustment to record on-hand quantities.`)
              }
            />
          }
        >
          <div class="rounded-lg border border-border bg-surface shadow-[var(--surface-shadow)]">
            <table class="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col class="w-[40%]" />
                <col class="w-[28%] max-sm:w-0" />
                <col class="w-[18%] max-sm:w-[32%]" />
                <col class="w-[14%] max-sm:w-[28%]" />
              </colgroup>
              <thead>
                <tr class="border-b border-border bg-surface-secondary/60 text-left text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  <th class="px-4 py-2.5">{t(msg`Product`)}</th>
                  <th class="px-4 py-2.5 max-sm:hidden">{t(msg`Location`)}</th>
                  <th class="px-4 py-2.5">{t(msg`Status`)}</th>
                  <th class="px-4 py-2.5 text-right">{t(msg`On-hand`)}</th>
                </tr>
              </thead>
              <tbody>
                <For each={visibleQuants()}>
                  {(q, i) => {
                    const onHand = () => fromMinorQty(q.qty ?? 0);
                    const status = () => deriveStockStatus(onHand());
                    const statusLabel = () =>
                      status() === "out" ? t(msg`Out of stock`) : t(msg`In stock`);
                    return (
                      <tr
                        class="list-row-in border-b border-border/50 last:border-0 odd:bg-surface even:bg-surface-secondary/30 transition-colors hover:bg-accent/5"
                        style={{ "--row-index": Math.min(i(), 12) }}
                        data-testid="onhand-row"
                        data-product-id={q.product_id}
                        data-location-id={q.location_id}
                      >
                        <td class="px-4 py-2.5 font-medium">
                          {/* Interconnect: drill into this product's lots/traceability. */}
                          <Link
                            to="/lots"
                            search={{ product: q.product_id ?? "" }}
                            class="inline-flex items-center gap-1 truncate hover:text-primary hover:underline"
                            title={t(msg`View lots for this product`)}
                          >
                            <Tags class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <span class="truncate">{productName()(q.product_id ?? "")}</span>
                          </Link>
                          {/* On mobile, location stacks under the product (the column is hidden). */}
                          <span class="mt-0.5 block truncate text-xs text-muted-foreground sm:hidden">
                            {locationName()(q.location_id ?? "")}
                          </span>
                        </td>
                        <td class="px-4 py-2.5 text-muted-foreground max-sm:hidden">
                          <span class="block truncate">{locationName()(q.location_id ?? "")}</span>
                        </td>
                        <td class="px-4 py-2.5">
                          <StockBadge status={status()} label={statusLabel()} />
                        </td>
                        <td
                          class="px-4 py-2.5 text-right font-mono font-medium tabular-nums"
                          data-numeric
                          data-testid="onhand-qty"
                        >
                          {onHand()}
                        </td>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </div>
    </PageContainer>
  );
}

export const stockRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock",
  validateSearch: (search: Record<string, unknown>): { product?: string } => ({
    product: typeof search.product === "string" ? search.product : undefined,
  }),
  component: StockPage,
});
